import {
  DateRange,
  StaticDateRangePicker,
} from '@automata/rexports/x-date-pickers-pro';
import {
  getDailyISOString,
  getDateRangeNormalizedValues,
} from '@automata/utils/time';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';

import { createColumnDependencies } from 'components/ModelList/columns';
import { useURLFilters } from 'hooks/useURLFilters';
import { useURLSearchParams } from 'hooks/useURLSearchParams';

import { FilterIds } from '../..';

interface Props {
  columnId: ReturnType<typeof createColumnDependencies>[number];
}

const DateRangePicker = ({ columnId }: Props) => {
  const theme = useTheme();
  const isLargerScreen = useMediaQuery(theme.breakpoints.up('sm'));
  const { updateFilterModelItems } = useURLFilters(columnId, true);
  const [searchParams] = useURLSearchParams();

  const startParam = searchParams[FilterIds.START_DATE];
  const endParam = searchParams[FilterIds.END_DATE];

  const datePickerStartValue = useMemo(
    () => (startParam ? dayjs(startParam) : null),
    [startParam]
  );
  const datePickerEndValue = useMemo(
    () => (endParam ? dayjs(endParam) : null),
    [endParam]
  );

  const handleDatePickerChange = useCallback(
    (value: DateRange<Dayjs>): void => {
      const [startValue, endValue] = getDateRangeNormalizedValues(value);
      updateFilterModelItems({
        [FilterIds.START_DATE]: getDailyISOString(startValue, 'start'),
        [FilterIds.END_DATE]: getDailyISOString(endValue, 'end'),
      });
    },
    [updateFilterModelItems]
  );

  const handleClearClick = useCallback(() => {
    updateFilterModelItems({
      [FilterIds.START_DATE]: null,
      [FilterIds.END_DATE]: null,
    });
  }, [updateFilterModelItems]);

  return (
    <Box position="relative">
      <StaticDateRangePicker
        displayStaticWrapperAs={isLargerScreen ? 'desktop' : 'mobile'}
        onChange={handleDatePickerChange}
        value={[datePickerStartValue, datePickerEndValue]}
        disableFuture
      />
      <Box
        position="absolute"
        bottom={10}
        right={isLargerScreen ? 10 : undefined}
        left={!isLargerScreen ? 10 : undefined}
      >
        <Button onClick={handleClearClick} size="small">
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default DateRangePicker;
